import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  FetchForecastRequest,
  GenerateForecastForAgreementResponse,
} from '@erp/rpc';
import type { CalculationType } from '@erp/shared-types';

// ============================================================================
// Fetch stored forecast data
// ============================================================================

interface ForecastParams {
  agreementId?: string;
  startDate?: string;
  endDate?: string;
  calculationTypes?: CalculationType[];
}

export function useRebateForecast(params: ForecastParams = {}) {
  const input: FetchForecastRequest = {
    ...(params.agreementId && { agreementId: params.agreementId }),
    ...(params.startDate && { startDate: params.startDate }),
    ...(params.endDate && { endDate: params.endDate }),
    ...(params.calculationTypes &&
      params.calculationTypes.length > 0 && {
        calculationTypes: params.calculationTypes,
      }),
  };

  return useQuery({
    ...orpc.rebates.forecast.fetch.queryOptions({
      input,
    }),
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    select: (response) => response.data,
  });
}

// ============================================================================
// Auto-generate forecast for agreement if stale
// ============================================================================

export function useAgreementForecastAutoGenerate(
  agreementId: string,
  params?: { startDate?: string; endDate?: string },
  onSuccess?: (data: GenerateForecastForAgreementResponse['data']) => void,
) {
  const queryResult = useQuery({
    ...orpc.rebates.forecast.generateForAgreement.queryOptions({
      input: {
        agreementId,
        ...(params?.startDate && { startDate: params.startDate }),
        ...(params?.endDate && { endDate: params.endDate }),
      },
    }),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    enabled: !!agreementId,
    select: (response) => response.data,
  });

  // Call onSuccess callback when data is successfully fetched
  // React Query v5 removed onSuccess from useQuery, so we use useEffect
  useEffect(() => {
    if (
      queryResult.data &&
      !queryResult.isLoading &&
      !queryResult.isError &&
      onSuccess
    ) {
      onSuccess(queryResult.data);
    }
    // onSuccess intentionally excluded from deps to avoid calling on every callback reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data, queryResult.isLoading, queryResult.isError]);

  return queryResult;
}

// ============================================================================
// Calendar year forecast (convenience wrapper)
// ============================================================================

export function useCalendarYearForecast(year?: number) {
  const params: ForecastParams = {};

  if (year !== undefined) {
    params.startDate = `${year}-01-01`;
    params.endDate = `${year}-12-31`;
  }

  return useRebateForecast(params);
}
